import { useCallback, useEffect, useRef, useState } from "react";
import { Emblem } from "@/components/Logo";
import {
  ChevronLeft, ChevronRight, Maximize, Minimize, Printer, NotebookPen,
  Users, Boxes, CalendarDays, GraduationCap, MessageCircle, Target,
  ShieldCheck, FileText, Rocket, Home, LayoutDashboard, Contact, ListChecks, Crown,
} from "lucide-react";

const IMG = "/presentation";

const SLIDES = [
  {
    kind: "cover",
    kicker: "Aprisco Church · Zona Norte",
    title: "Plataforma de Gestão de PGDs",
    subtitle: "Pequenos Grupos de Discipulado — cuidando de pessoas, multiplicando líderes.",
    icon: Rocket,
    notes: [
      "Boas-vindas. Esta é a ferramenta que vai organizar toda a vida dos nossos PGDs.",
      "Em poucos minutos vamos passear por tudo o que ela faz.",
    ],
  },
  {
    kind: "text",
    kicker: "Por que existe",
    title: "O desafio que resolvemos",
    icon: Target,
    bullets: [
      "Perder o controle de grupos, pessoas e combinados no papel e no WhatsApp.",
      "Falta de visão de frequência, formação e multiplicação.",
      "Dificuldade de acompanhar o que cada líder assumiu.",
      "A plataforma centraliza tudo em um só lugar, simples e visual.",
    ],
    notes: [
      "Hoje a informação fica espalhada — cabeça, caderno, grupos de WhatsApp.",
      "A proposta é ter uma fonte única de verdade para a liderança.",
    ],
  },
  {
    kind: "grid",
    kicker: "Visão geral",
    title: "O que a plataforma oferece",
    icon: Boxes,
    items: [
      { icon: Home, label: "Página pública", desc: "Encontrar um PGD" },
      { icon: LayoutDashboard, label: "Painel", desc: "Indicadores do dia" },
      { icon: Boxes, label: "Grupos", desc: "Cadastro e multiplicação" },
      { icon: ListChecks, label: "Frequência", desc: "Presença por encontro" },
      { icon: GraduationCap, label: "Formação", desc: "Estudos e escola" },
      { icon: CalendarDays, label: "Calendário", desc: "Eventos e agenda" },
      { icon: Contact, label: "Contatos", desc: "Pessoas dos grupos" },
      { icon: MessageCircle, label: "Atendimento", desc: "WhatsApp" },
      { icon: Target, label: "Metas", desc: "Compromissos assumidos" },
      { icon: Crown, label: "Painel do Pastor", desc: "Visão da liderança" },
      { icon: ShieldCheck, label: "Acessos", desc: "Segurança e níveis" },
      { icon: FileText, label: "Relatórios", desc: "PDFs para imprimir" },
    ],
    notes: ["Um mapa rápido dos módulos. Vamos ver cada um na prática a seguir."],
  },
  {
    kind: "shot", img: "landing", kicker: "Para a comunidade", title: "Página inicial pública", icon: Home,
    bullets: [
      "Qualquer pessoa encontra um PGD perto de casa.",
      "Vê endereço, bairro e abre a localização no mapa.",
      "Botão de suporte no WhatsApp para tirar dúvidas.",
    ],
    notes: ["Esta é a porta de entrada — não precisa de login.", "O visitante já consegue pedir ajuda pelo WhatsApp."],
  },
  {
    kind: "shot", img: "usuarios", kicker: "Organização", title: "Acesso e níveis", icon: Users,
    bullets: [
      "Hierarquia: Pastor Sênior, Coordenadores, Supervisores e Líderes.",
      "Cada nível vê e faz o que é do seu escopo.",
      "Cadastros passam por aprovação da liderança.",
    ],
    notes: ["A ferramenta respeita a nossa estrutura de liderança.", "Ninguém vê dados que não são da sua responsabilidade."],
  },
  {
    kind: "shot", img: "dashboard", kicker: "O dia a dia", title: "Painel (início)", icon: LayoutDashboard,
    bullets: [
      "Visão geral: PGDs ativos, membros, frequência e líderes formados.",
      "Entrada da semana: estudo, visão, preparação e dica.",
      "Avisos de pendências logo ao entrar.",
    ],
    notes: ["Ao logar, o líder já sabe o que precisa de atenção.", "Os números são atualizados automaticamente."],
  },
  {
    kind: "shot", img: "grupos", kicker: "Coração do sistema", title: "Grupos (PGDs)", icon: Boxes,
    bullets: [
      "Cadastro com líderes, dia, horário e endereço.",
      "Ativos e inativos, com data de início.",
      "Árvore de multiplicação — de onde cada grupo nasceu.",
    ],
    notes: ["Aqui vive a informação de cada grupo.", "A multiplicação fica registrada e visível."],
  },
  {
    kind: "shot", img: "frequencia", kicker: "Cuidado", title: "Frequência", icon: ListChecks,
    bullets: [
      "Check-in de presença a cada encontro.",
      "Visitantes, oferta e resumo da palavra.",
      "Histórico dos encontros recentes.",
    ],
    notes: ["Registrar presença ajuda a perceber quem está se afastando.", "Tudo em poucos toques."],
  },
  {
    kind: "shot", img: "formacao", kicker: "Discipulado", title: "Formação", icon: GraduationCap,
    bullets: [
      "Estudos do mês para os grupos.",
      "Escola de Liderança em módulos.",
      "Acompanhamento de progresso de cada líder.",
    ],
    notes: ["Conteúdo padronizado para todos os PGDs.", "O líder marca o que já concluiu."],
  },
  {
    kind: "shot", img: "calendario", kicker: "Agenda", title: "Calendário", icon: CalendarDays,
    bullets: [
      "Reuniões, capacitações e celebrações.",
      "Próximos eventos e histórico.",
      "Fases de implantação dos PGDs.",
    ],
    notes: ["Todo mundo enxerga a mesma agenda.", "Evita conflito de datas."],
  },
  {
    kind: "shot", img: "contatos", kicker: "Pessoas", title: "Contatos e participantes", icon: Contact,
    bullets: [
      "Cadastro de interessados e participantes.",
      "Vínculo com o grupo certo.",
      "Base para o cuidado e o atendimento.",
    ],
    notes: ["Ninguém que chega se perde.", "Cada contato vira uma oportunidade de cuidado."],
  },
  {
    kind: "shot", img: "atendimento", kicker: "Relacionamento", title: "Atendimento por WhatsApp", icon: MessageCircle,
    bullets: [
      "Falar com membros direto pelo WhatsApp.",
      "Modelos de mensagem prontos e editáveis.",
      "Envio para vários contatos de uma vez.",
    ],
    notes: ["Usa o WhatsApp que todos já têm — sem custo extra.", "As mensagens já vão personalizadas com o nome."],
  },
  {
    kind: "shot", img: "metas_lista", kicker: "Não perder nada de vista", title: "Metas & Compromissos", icon: Target,
    bullets: [
      "Registrar o que foi combinado com pessoas e liderança.",
      "Prazo, prioridade, categoria e responsável.",
      "Visão em Lista e em Kanban.",
    ],
    notes: ["Aqui entra o grande diferencial: acompanhar os combinados.", "Do Pastor com a liderança, do líder com os participantes."],
  },
  {
    kind: "shot", img: "metas_kanban", kicker: "Acompanhamento", title: "Diário, lembretes e recorrência", icon: ListChecks,
    bullets: [
      "Diário de andamento em cada compromisso.",
      "Lembretes de atrasados e do que está vencendo.",
      "Compromissos que se repetem toda semana ou mês.",
    ],
    notes: ["Arraste no Kanban para mudar o status.", "Cada retorno fica registrado no diário."],
  },
  {
    kind: "shot", img: "painel_pastor", kicker: "Visão de cima", title: "Painel do Pastor + Notificações", icon: Crown,
    bullets: [
      "Visão geral dos compromissos de toda a liderança.",
      "Por líder e por supervisor, com atrasados em destaque.",
      "Avisos no app quando algo é delegado a você.",
    ],
    notes: ["O Pastor enxerga a liderança inteira numa tela.", "Resumo semanal automático toda segunda-feira."],
  },
  {
    kind: "shot", img: "usuarios", kicker: "Confiança", title: "Segurança e acessos", icon: ShieldCheck,
    bullets: [
      "Aprovação de novos cadastros.",
      "Redefinição de senha pela coordenação.",
      "Cada pessoa acessa apenas o que é seu.",
    ],
    notes: ["Segurança pensada para proteger os dados das pessoas.", "Sem senhas expostas na tela."],
  },
  {
    kind: "shot", img: "relatorios", kicker: "Prestação de contas", title: "Relatórios em PDF", icon: FileText,
    bullets: [
      "Relatório do supervisor e da liderança.",
      "Metas & compromissos por líder ou grupo.",
      "Prontos para imprimir e arquivar.",
    ],
    notes: ["Tudo que se registra pode virar um relatório.", "Ótimo para reuniões e prestação de contas."],
  },
  {
    kind: "cover",
    kicker: "Vamos juntos",
    title: "Como acessar + próximos passos",
    subtitle: "apriscochurch.app",
    icon: Rocket,
    footer: "Cadastre-se, aguarde a aprovação e comece a cuidar da sua gente.",
    notes: [
      "O site é apriscochurch.app — abra no celular ou no computador.",
      "Convide a liderança a se cadastrar e mãos à obra!",
    ],
  },
];

export default function Presentation() {
  const [idx, setIdx] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [isFs, setIsFs] = useState(false);
  const touch = useRef(null);
  const total = SLIDES.length;

  const go = useCallback((d) => setIdx((i) => Math.min(total - 1, Math.max(0, i + d))), [total]);
  const goto = useCallback((n) => setIdx(Math.min(total - 1, Math.max(0, n))), [total]);

  const toggleFs = useCallback(async () => {
    try {
      if (!document.fullscreenElement) { await document.documentElement.requestFullscreen(); }
      else { await document.exitFullscreen(); }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (["ArrowRight", "PageDown", " "].includes(e.key)) { e.preventDefault(); go(1); }
      else if (["ArrowLeft", "PageUp"].includes(e.key)) { e.preventDefault(); go(-1); }
      else if (e.key === "Home") goto(0);
      else if (e.key === "End") goto(total - 1);
      else if (e.key.toLowerCase() === "n") setShowNotes((s) => !s);
      else if (e.key.toLowerCase() === "f") toggleFs();
      else if (e.key.toLowerCase() === "p") { e.preventDefault(); window.print(); }
    };
    const onFs = () => setIsFs(!!document.fullscreenElement);
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    return () => { window.removeEventListener("keydown", onKey); document.removeEventListener("fullscreenchange", onFs); };
  }, [go, goto, total, toggleFs]);

  const s = SLIDES[idx];

  return (
    <div className="presentation-root min-h-screen w-full bg-[#0B0F17] text-slate-100 overflow-hidden relative select-none"
      data-testid="presentation-root"
      onTouchStart={(e) => { touch.current = e.changedTouches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touch.current == null) return;
        const dx = e.changedTouches[0].clientX - touch.current;
        if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
        touch.current = null;
      }}>

      {/* ambient glows */}
      <div className="pointer-events-none absolute -top-40 -right-40 h-[32rem] w-[32rem] rounded-full bg-[#EA580C]/20 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-52 -left-40 h-[34rem] w-[34rem] rounded-full bg-[#F59E0B]/10 blur-[130px]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "26px 26px" }} />

      {/* top bar */}
      <header className="relative z-20 flex items-center justify-between px-6 sm:px-10 py-5">
        <div className="flex items-center gap-3">
          <Emblem className="h-8 w-8" />
          <div className="leading-none">
            <div className="font-display font-extrabold tracking-tight text-base bg-gradient-to-r from-[#F59E0B] to-[#EA580C] bg-clip-text text-transparent">APRISCO CHURCH</div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 mt-1">Zona Norte · Gestão PGD</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowNotes((v) => !v)} data-testid="toggle-notes-button"
            className={`hidden sm:inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${showNotes ? "bg-[#EA580C] text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            <NotebookPen className="h-4 w-4" /> Notas
          </button>
          <button onClick={() => window.print()} data-testid="print-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-white/5 text-slate-300 hover:bg-white/10 transition-colors">
            <Printer className="h-4 w-4" /> PDF
          </button>
          <button onClick={toggleFs} data-testid="fullscreen-button"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-white/5 text-slate-300 hover:bg-white/10 transition-colors">
            {isFs ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
            <span className="hidden sm:inline">{isFs ? "Sair" : "Tela cheia"}</span>
          </button>
        </div>
      </header>

      {/* slide */}
      <main className="relative z-10 px-6 sm:px-12 lg:px-20" style={{ minHeight: "calc(100vh - 200px)" }}>
        <div key={idx} className="slide-anim h-full">
          <Slide s={s} />
        </div>
      </main>

      {/* presenter notes */}
      {showNotes && (
        <div className="relative z-20 mx-6 sm:mx-12 lg:mx-20 mb-2 rounded-2xl border border-white/10 bg-black/40 backdrop-blur px-6 py-4" data-testid="presenter-notes">
          <div className="text-[11px] uppercase tracking-widest text-[#F59E0B] font-semibold mb-1">Notas do apresentador</div>
          <ul className="list-disc pl-5 text-sm text-slate-300 space-y-1">
            {(s.notes || []).map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      )}

      {/* controls / progress */}
      <footer className="relative z-20 px-6 sm:px-12 lg:px-20 py-5">
        <div className="flex items-center gap-4">
          <button onClick={() => go(-1)} disabled={idx === 0} data-testid="prev-slide-button"
            className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-30 transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button onClick={() => go(1)} disabled={idx === total - 1} data-testid="next-slide-button"
            className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-[#EA580C] hover:bg-[#C2410C] disabled:opacity-30 transition-colors">
            <ChevronRight className="h-5 w-5" />
          </button>

          <div className="flex-1 mx-2">
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#F59E0B] to-[#EA580C] transition-all duration-500"
                style={{ width: `${((idx + 1) / total) * 100}%` }} />
            </div>
          </div>

          <div className="text-sm text-slate-400 tabular-nums" data-testid="slide-indicator">
            Slide <span className="text-slate-100 font-semibold">{idx + 1}</span> de {total}
          </div>
        </div>
        <div className="mt-3 hidden md:flex flex-wrap gap-1.5">
          {SLIDES.map((_, i) => (
            <button key={i} onClick={() => goto(i)} aria-label={`Ir para slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-7 bg-[#EA580C]" : "w-3 bg-white/15 hover:bg-white/30"}`} />
          ))}
        </div>
      </footer>

      {/* click zones for navigation (below content, above bg) */}
      <button aria-label="anterior" onClick={() => go(-1)} className="hidden lg:block absolute left-0 top-24 bottom-28 w-[12%] z-0 cursor-w-resize" />
      <button aria-label="próximo" onClick={() => go(1)} className="hidden lg:block absolute right-0 top-24 bottom-28 w-[12%] z-0 cursor-e-resize" />

      {/* PRINT: all slides stacked */}
      <div className="print-only">
        {SLIDES.map((sl, i) => (
          <div className="print-page" key={i}>
            <div className="print-head">
              <span className="print-kicker">{sl.kicker}</span>
              <span className="print-num">{i + 1} / {total}</span>
            </div>
            <h2 className="print-title">{sl.title}</h2>
            {sl.subtitle && <p className="print-sub">{sl.subtitle}</p>}
            <div className="print-body">
              {sl.bullets && <ul>{sl.bullets.map((b, j) => <li key={j}>{b}</li>)}</ul>}
              {sl.items && <ul className="print-grid">{sl.items.map((it, j) => <li key={j}><b>{it.label}</b> — {it.desc}</li>)}</ul>}
              {sl.img && <img src={`${IMG}/${sl.img}.png`} alt={sl.title} />}
            </div>
            {sl.footer && <p className="print-sub">{sl.footer}</p>}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideIn { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        .slide-anim { animation: slideIn .5s cubic-bezier(.2,.7,.2,1) both; }
        .print-only { display: none; }
        @media print {
          .presentation-root > *:not(.print-only) { display: none !important; }
          .print-only { display: block !important; }
          .print-page { page-break-after: always; padding: 28px 34px; color: #0B0F17; }
          .print-head { display: flex; justify-content: space-between; font-size: 12px; color: #C2410C; text-transform: uppercase; letter-spacing: .12em; font-weight: 700; }
          .print-title { font-size: 26px; font-weight: 800; margin: 6px 0 4px; }
          .print-sub { color: #475569; font-size: 15px; margin: 2px 0 10px; }
          .print-body ul { font-size: 15px; line-height: 1.6; margin: 6px 0 12px 18px; }
          .print-body img { width: 100%; max-height: 62vh; object-fit: contain; border: 1px solid #E2E8F0; border-radius: 10px; }
          .print-grid { columns: 2; }
          @page { size: landscape; margin: 8mm; }
        }
      `}</style>
    </div>
  );
}

function Slide({ s }) {
  const Icon = s.icon || Target;

  if (s.kind === "cover") {
    return (
      <div className="flex flex-col items-center justify-center text-center h-full pt-6 pb-10" data-testid="slide-cover">
        <div className="mb-8 inline-flex items-center justify-center h-24 w-24 rounded-3xl bg-gradient-to-br from-[#F59E0B]/20 to-[#EA580C]/20 border border-white/10">
          <Icon className="h-11 w-11 text-[#F59E0B]" />
        </div>
        <div className="text-sm font-semibold uppercase tracking-[0.3em] text-[#F59E0B] mb-5">{s.kicker}</div>
        <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-5xl leading-[1.05] bg-gradient-to-br from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">
          {s.title}
        </h1>
        {s.subtitle && <p className="mt-6 text-lg sm:text-2xl text-[#F59E0B] font-semibold">{s.subtitle}</p>}
        {s.footer && <p className="mt-4 text-base sm:text-lg text-slate-400 max-w-2xl">{s.footer}</p>}
      </div>
    );
  }

  if (s.kind === "text") {
    return (
      <div className="max-w-5xl h-full flex flex-col justify-center py-6" data-testid="slide-text">
        <Kicker icon={Icon} label={s.kicker} />
        <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl mb-8">{s.title}</h2>
        <ul className="space-y-4">
          {s.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-4 text-lg sm:text-2xl text-slate-200" style={{ animationDelay: `${i * 90}ms` }}>
              <span className="mt-2.5 h-2.5 w-2.5 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#EA580C] shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (s.kind === "grid") {
    return (
      <div className="h-full flex flex-col justify-center py-6" data-testid="slide-grid">
        <Kicker icon={Icon} label={s.kicker} />
        <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl mb-8">{s.title}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {s.items.map((it, i) => {
            const II = it.icon;
            return (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5 hover:bg-white/[0.06] transition-colors">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#EA580C]/15 mb-3">
                  <II className="h-5 w-5 text-[#F59E0B]" />
                </div>
                <div className="font-semibold text-base sm:text-lg">{it.label}</div>
                <div className="text-sm text-slate-400">{it.desc}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // shot
  return (
    <div className="h-full grid lg:grid-cols-[38%_1fr] gap-8 lg:gap-12 items-center py-6" data-testid="slide-shot">
      <div>
        <Kicker icon={Icon} label={s.kicker} />
        <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl mb-6">{s.title}</h2>
        <ul className="space-y-3.5">
          {(s.bullets || []).map((b, i) => (
            <li key={i} className="flex items-start gap-3 text-base sm:text-xl text-slate-200">
              <span className="mt-2 h-2 w-2 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#EA580C] shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="relative">
        <div className="absolute -inset-4 bg-gradient-to-br from-[#EA580C]/20 to-[#F59E0B]/10 blur-2xl rounded-3xl" />
        <div className="relative rounded-2xl border border-white/12 bg-black/30 shadow-2xl overflow-hidden ring-1 ring-white/5">
          <img src={`${IMG}/${s.img}.png`} alt={s.title} className="w-full h-auto block" loading="eager" />
        </div>
      </div>
    </div>
  );
}

function Kicker({ icon: Icon, label }) {
  return (
    <div className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.22em] text-[#F59E0B] mb-4">
      <Icon className="h-4 w-4" /> {label}
    </div>
  );
}
